import apiV2 from '../api/v2';

interface ApiEnvelope<T> {
    success: boolean;
    data?: T;
    error?: string;
}

export interface InternalImageUploadResponse {
    url: string;
    fileId: string;
    objectPath: string;
    width?: number;
    height?: number;
}

/**
 * The set of surfaces allowed to upload internal (non-patient-facing) images.
 * Must stay in sync with the backend's Zod enum — an unlisted or missing value
 * is rejected server-side with a 400 VALIDATION_ERROR.
 */
export type InternalImageContext =
    'dx-report' | 'note' | 'vca' | 'instructions' | 'work-queue-hold';

/**
 * Read an image file's natural width/height in the browser. Used to size the
 * Tiptap image node so the editor and the eventual PDF can lay out around
 * it without re-fetching the image.
 */
function measureImage(file: File): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            const dims = { width: img.naturalWidth, height: img.naturalHeight };
            URL.revokeObjectURL(url);
            resolve(dims);
        };
        img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('Failed to read image dimensions'));
        };
        img.src = url;
    });
}

/**
 * Upload an inline internal image and return its public GCS URL plus the
 * locally-measured dimensions. Called from the Tiptap toolbar button, paste
 * handler, and drop handler across dx-report, VCA, notes, instructions, and
 * work-queue-hold editors. `context` tags which surface the upload came from
 * and is required by the backend — there is no default.
 *
 * Throws on validation failure or network error so the caller can show a
 * toast/inline error.
 */
export async function uploadInternalImage(
    patientId: string,
    file: File,
    context: InternalImageContext
): Promise<InternalImageUploadResponse> {
    const allowedMime = new Set(['image/jpeg', 'image/png', 'image/webp']);
    if (!allowedMime.has(file.type)) {
        throw new Error('Image must be JPEG, PNG, or WebP.');
    }
    const MAX_BYTES = 8 * 1024 * 1024;
    if (file.size > MAX_BYTES) {
        throw new Error('Image exceeds the 8MB size limit.');
    }

    // Measure first — if the browser can't decode it we want to fail before
    // we burn a network round-trip on a corrupt file.
    const dims = await measureImage(file);

    const form = new FormData();
    form.append('image', file, file.name);
    form.append('context', context);

    const response = await apiV2.post<ApiEnvelope<InternalImageUploadResponse>>(
        `/patients/${encodeURIComponent(patientId)}/internal/images`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } }
    );

    const body = response.data;
    if (!body?.success || !body.data) {
        throw new Error(body?.error ?? 'Failed to upload image.');
    }

    return { ...body.data, width: dims.width, height: dims.height };
}

export async function resolveInternalImageUrls(
    patientId: string,
    fileIds: string[]
): Promise<Record<string, string>> {
    if (fileIds.length === 0) return {};
    const response = await apiV2.post<ApiEnvelope<{ urls: Record<string, string> }>>(
        `/patients/${encodeURIComponent(patientId)}/internal/images/resolve`,
        { fileIds }
    );
    const body = response.data;
    if (!body?.success || !body.data) {
        throw new Error(body?.error ?? 'Failed to resolve image URLs.');
    }
    return body.data.urls;
}
